import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { createOrderReport, getStaffConfig, type ReportType } from "@/api/staff";
import { uploadImage } from "@/api/upload";
import { useWorkspaceStore } from "@/store/useWorkspaceStore";
import { toastSuccess, toastWarning } from "@/utils/toast";

const BRAND = "#FE7A3A";

export default function WorkspaceOrderReportPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const orderId = Number(searchParams.get("id") || "0");

  const config = useWorkspaceStore((s) => s.config);
  const setConfig = useWorkspaceStore((s) => s.setConfig);

  const reportTypes = useMemo<ReportType[]>(
    () => (config?.massage_report_type ?? []) as unknown as ReportType[],
    [config],
  );
  const serviceTel = config?.service_tel ?? "";

  const [selectedTypeId, setSelectedTypeId] = useState<number | null>(null);
  const selectedType = useMemo(
    () => reportTypes.find((t) => t.id === selectedTypeId) ?? null,
    [reportTypes, selectedTypeId],
  );
  const [content, setContent] = useState("");
  const [imgs, setImgs] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (config) return;
    const load = async () => {
      try {
        const c = await getStaffConfig();
        setConfig(c);
      } catch {
        // toast 已在 request 层处理
      }
    };
    load();
  }, [config, setConfig]);

  const onPickFiles = async (files: FileList | null) => {
    if (!files?.length) return;
    if (imgs.length >= 4) return toastWarning("最多上传4张图片");

    const picked = Array.from(files).slice(0, 4 - imgs.length);
    setUploading(true);
    try {
      const uploaded = await Promise.all(picked.map((f) => uploadImage(f)));
      setImgs((p) => [...p, ...uploaded].slice(0, 4));
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const removeImg = (idx: number) => {
    setImgs((p) => p.filter((_, i) => i !== idx));
  };

  const submit = async () => {
    if (!orderId) return toastWarning("缺少订单ID");
    if (!selectedType) return toastWarning("请选择报备类型");
    if (!content.trim()) return toastWarning("请输入报备描述");

    if (submitting) return toastWarning("操作频繁，请稍后再试");
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("order_id", String(orderId));
      fd.append("type", selectedType.name);
      fd.append("content", content.trim());
      imgs.forEach((u) => fd.append("imgs[]", u));

      await createOrderReport(fd);
      toastSuccess("提交成功");
      setTimeout(() => navigate(-1), 800);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f7f7f7]" style={{ fontFamily: "'SF Pro Text','PingFang SC','Inter',system-ui,sans-serif" }}>
      {/* 顶部栏 */}
      <div className="sticky top-0 z-20" style={{ background: BRAND }}>
        <div className="px-5 flex items-center gap-3" style={{ paddingTop: "calc(env(safe-area-inset-top,0px) + 16px)", paddingBottom: 13 }}>
          <button
            onClick={() => navigate(-1)}
            className="w-9 h-9 flex items-center justify-center rounded-full cursor-pointer active:scale-[0.98] shrink-0"
            style={{ background: "rgba(255,255,255,0.2)" }}
            aria-label="返回"
          >
            <i className="ri-arrow-left-line" style={{ fontSize: 20, color: "#fff" }} />
          </button>
          <span className="text-lg font-bold text-white">报备</span>
        </div>
      </div>

      <div className="px-4 pt-4 pb-10">
        <div className="bg-white rounded-2xl p-4 mb-5">
          {/* 报备类型 */}
          <div className="pb-4 border-b border-gray-100">
            <div className="text-base font-bold mb-3">报备类型</div>
            {reportTypes.length ? (
              <div className="flex flex-col gap-3">
                {reportTypes.map((t) => (
                  <label key={t.id} className="flex items-center gap-3 cursor-pointer select-none">
                    <input
                      type="radio"
                      name="reportType"
                      value={String(t.id)}
                      checked={selectedTypeId === t.id}
                      onChange={() => setSelectedTypeId(t.id)}
                    />
                    <span className="text-sm text-gray-900">{t.name}</span>
                  </label>
                ))}
              </div>
            ) : (
              <div className="text-sm text-gray-400">暂无报备类型配置</div>
            )}
          </div>

          {/* 报备描述 */}
          <div className="pt-4 mb-4">
            <div className="text-base font-bold mb-3">报备描述</div>
            <div className="rounded-2xl bg-[#f8f8f8] p-3">
              <textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder="请输入详细的报备描述"
                className="w-full text-sm bg-transparent outline-none resize-none min-h-[120px]"
              />
            </div>
          </div>

          {/* 报备证明 */}
          <div className="mb-2">
            <div className="text-base font-bold mb-3">报备证明</div>
            <div className="flex flex-wrap gap-2">
              {imgs.map((u, idx) => (
                <div key={`${u}-${idx}`} className="relative w-[70px] h-[70px]">
                  <img src={u} alt="" className="w-full h-full rounded-2xl object-cover" />
                  <button
                    type="button"
                    onClick={() => removeImg(idx)}
                    className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-white shadow flex items-center justify-center active:scale-[0.98]"
                    aria-label="删除图片"
                  >
                    <i className="ri-close-line text-gray-500" />
                  </button>
                </div>
              ))}

              {imgs.length < 4 && (
                <button
                  type="button"
                  disabled={uploading}
                  onClick={() => fileInputRef.current?.click()}
                  className={`w-[70px] h-[70px] rounded-2xl bg-[#f8f8f8] flex items-center justify-center active:scale-[0.98] ${uploading ? "opacity-60" : ""}`}
                  aria-label="上传图片"
                  title="上传图片"
                >
                  <i className="ri-add-line text-2xl text-gray-400" />
                </button>
              )}
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              hidden
              onChange={(e) => onPickFiles(e.target.files)}
            />
          </div>
        </div>

        <div className="flex flex-col items-center">
          {serviceTel && (
            <button
              type="button"
              onClick={() => {
                window.location.href = `tel:${serviceTel}`;
              }}
              className="mb-4 text-sm text-[#fe7a3a] border-b border-[#fe7a3a] pb-1"
            >
              客服电话：{serviceTel}
            </button>
          )}

          <button
            type="button"
            onClick={submit}
            disabled={submitting}
            className={`w-full py-3 text-center text-white rounded-[999px] font-semibold active:scale-[0.98] ${submitting ? "opacity-60" : ""}`}
            style={{ background: BRAND }}
          >
            {submitting ? "提交中..." : "提交"}
          </button>
        </div>
      </div>
    </div>
  );
}

